import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { NodeTreeItem, NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/masters/store-events` (v0.4 章④ F-3、D-53)。
 * ⚠️ シフト専用マスタにしない — 需要を説明する共通マスタ (KPI 異常値の説明・AI 異常検知の
 * 誤報抑止にも使う)。登録主体は店長 (staffing:manage own_store で足りる)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-4 / §4-11
 */

type EventType = { event_type_id: string; code: string; label: string; color: string | null; sort_order: number };
type StoreEvent = {
  event_id: string; node_id: string; event_type_id: string | null;
  start_date: string; end_date: string; title: string; description: string | null;
  expected_demand_factor: number | null;
};

const today = () => new Date().toISOString().slice(0, 10);
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type EventDraft = {
  event_id: string | null;
  node_id: string;
  event_type_id: string;
  start_date: string;
  end_date: string;
  title: string;
  description: string;
  expected_demand_factor: string;
};

const EMPTY_EVENT_DRAFT: EventDraft = {
  event_id: null, node_id: '', event_type_id: '', start_date: today(), end_date: today(),
  title: '', description: '', expected_demand_factor: '',
};

function EventTypesTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{ event_type_id: string | null; code: string; label: string; color: string; sort_order: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventType | null>(null);

  const { data, isLoading } = useQuery<{ event_types: EventType[] }>({
    queryKey: ['store-event-types'],
    queryFn: () => apiFetch<{ event_types: EventType[] }>('/store-events/types'),
  });
  const types = data?.event_types ?? [];

  const saveMut = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => {
      const payload = { code: d.code.trim(), label: d.label.trim(), color: d.color.trim() || null, sort_order: Number(d.sort_order) || 0 };
      return d.event_type_id
        ? apiFetch(`/store-events/types/${d.event_type_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/store-events/types', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['store-event-types'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/store-events/types/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['store-event-types'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub">祭り・イベント等の種別を登録します。イベント登録時に選択できます。</p>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({ event_type_id: null, code: '', label: '', color: '', sort_order: String(types.length) })}>
            種別を追加
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : types.length === 0 ? (
        <EmptyState message="イベント種別がまだありません"
          description={canManage ? '「種別を追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {types.map((t) => (
            <li key={t.event_type_id} className="flex items-center justify-between gap-2 border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0 flex items-center gap-2">
                {t.color && <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: t.color }} aria-hidden />}
                <span className="text-sm font-semibold text-text-body break-words">{t.label}</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{t.code}</span>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" aria-label="編集" onClick={() => setDraft({ event_type_id: t.event_type_id, code: t.code, label: t.label, color: t.color ?? '', sort_order: String(t.sort_order) })}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                  <button type="button" aria-label="削除" onClick={() => setDeleteTarget(t)}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.event_type_id ? '種別を編集' : '種別を追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} placeholder="例: matsuri" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 地域の祭り" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">色 (任意)</label>
              <input value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} maxLength={20} className={inputCls} placeholder="#FE6A03" />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="イベント種別を削除" message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.event_type_id)} />
    </div>
  );
}

function EventFormModal({ draft, setDraft, flatNodes, eventTypes, onClose, onSaved }: {
  draft: EventDraft;
  setDraft: (d: EventDraft) => void;
  flatNodes: Array<{ node: NodeTreeItem; depth: number }>;
  eventTypes: EventType[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        node_id: draft.node_id,
        event_type_id: draft.event_type_id || null,
        start_date: draft.start_date,
        end_date: draft.end_date,
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        expected_demand_factor: draft.expected_demand_factor.trim() === '' ? null : Number(draft.expected_demand_factor),
      };
      return draft.event_id
        ? apiFetch(`/store-events/${draft.event_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/store-events', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSave = !!draft.node_id && draft.title.trim() !== '' && draft.start_date !== '' && draft.end_date !== '' && draft.start_date <= draft.end_date;

  return (
    <Modal open title={draft.event_id ? 'イベントを編集' : '新しいイベント'} onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-3">
        <div>
          <label className="block text-sm text-text-sub mb-1">対象店舗</label>
          <Select value={draft.node_id || undefined} onValueChange={(v) => setDraft({ ...draft, node_id: v })} disabled={!!draft.event_id}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="ノードを選ぶ" /></SelectTrigger>
            <SelectContent>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">開始日</label>
            <input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">終了日</label>
            <input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">種別 (任意)</label>
          <Select value={draft.event_type_id || undefined} onValueChange={(v) => setDraft({ ...draft, event_type_id: v })}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="種別を選ぶ" /></SelectTrigger>
            <SelectContent>
              {eventTypes.map((t) => <SelectItem key={t.event_type_id} value={t.event_type_id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">タイトル</label>
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 自由が丘女神祭り" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">説明 (任意)</label>
          <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={2000} rows={3}
            className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">需要係数 (任意、平常=1.0)</label>
          <input type="number" step="0.1" value={draft.expected_demand_factor} onChange={(e) => setDraft({ ...draft, expected_demand_factor: e.target.value })}
            className={inputCls} placeholder="例: 1.5" />
        </div>
      </div>
    </Modal>
  );
}

function EventsTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [nodeFilter, setNodeFilter] = useState<string>('');
  const [draft, setDraft] = useState<EventDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StoreEvent | null>(null);

  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const nodeNameById = useMemo(() => new Map(flatNodes.map(({ node }) => [node.node_id, node.name])), [flatNodes]);

  const { data: typesData } = useQuery<{ event_types: EventType[] }>({
    queryKey: ['store-event-types'], queryFn: () => apiFetch<{ event_types: EventType[] }>('/store-events/types'),
  });
  const eventTypes = typesData?.event_types ?? [];
  const typeById = useMemo(() => new Map(eventTypes.map((t) => [t.event_type_id, t])), [eventTypes]);

  const { data, isLoading } = useQuery<{ events: StoreEvent[] }>({
    queryKey: ['store-events', nodeFilter],
    queryFn: () => apiFetch<{ events: StoreEvent[] }>(`/store-events${nodeFilter ? `?node_id=${encodeURIComponent(nodeFilter)}` : ''}`),
  });
  const events = data?.events ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/store-events/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['store-events'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const onSaved = () => { qc.invalidateQueries({ queryKey: ['store-events'] }); setDraft(null); };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0 w-full sm:w-64">
          <label className="block text-xs text-text-sub mb-1">店舗で絞り込み</label>
          <Select value={nodeFilter || '__all__'} onValueChange={(v) => setNodeFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="全店舗" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">全店舗 (閲覧範囲)</SelectItem>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft({ ...EMPTY_EVENT_DRAFT, node_id: nodeFilter })}>
            イベントを登録
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : events.length === 0 ? (
        <EmptyState message="イベントがまだありません" description="祭り等、需要が普段と変わるイベントを1ヶ月以上前に登録しておくと、必要人員の判断材料になります。" />
      ) : (
        <ul className="space-y-2">
          {events.map((ev) => {
            const type = ev.event_type_id ? typeById.get(ev.event_type_id) : null;
            return (
              <li key={ev.event_id} className="border border-surface-border rounded-lg bg-white p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1 basis-full sm:basis-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-text-body break-words">{ev.title}</span>
                      {type && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{type.label}</span>
                      )}
                    </div>
                    <p className="text-xs text-text-sub mt-1">
                      {nodeNameById.get(ev.node_id) ?? ev.node_id} ・ {ev.start_date}〜{ev.end_date}
                      {ev.expected_demand_factor != null && ` ・需要係数 ${ev.expected_demand_factor}`}
                    </p>
                    {ev.description && <p className="text-xs text-text-sub mt-1 break-words">{ev.description}</p>}
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" aria-label="編集" onClick={() => setDraft({
                        event_id: ev.event_id, node_id: ev.node_id, event_type_id: ev.event_type_id ?? '',
                        start_date: ev.start_date, end_date: ev.end_date, title: ev.title,
                        description: ev.description ?? '', expected_demand_factor: ev.expected_demand_factor?.toString() ?? '',
                      })} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                      <button type="button" aria-label="削除" onClick={() => setDeleteTarget(ev)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {draft && <EventFormModal draft={draft} setDraft={setDraft} flatNodes={flatNodes} eventTypes={eventTypes} onClose={() => setDraft(null)} onSaved={onSaved} />}

      <ConfirmDialog open={!!deleteTarget} title="イベントを削除" message={`「${deleteTarget?.title}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.event_id)} />
    </div>
  );
}

export function StoreEventsPage() {
  const user = useAuthStore((s) => s.user);
  // イベント本体 (POST/PUT/DELETE /store-events) は isResourceInPermissionScope による
  // ノード単位判定のため own_store の店長でも自店イベントは正当に登録できる。
  const canManage = hasPermission(user?.effective_permissions, 'staffing', 'manage');
  // ⚠️ TD-22: イベント「種別」だけは requireTenantWideManage (routes/store-events.ts) で
  // 全店舗スコープを要求する (種別はテナント全体で共有されるマスタのため)。同じ canManage を
  // 両タブへ渡すと、店長には押しても必ず 403 になるボタンが出る。
  const canManageTypes = hasTenantWidePermission(user?.effective_permissions, 'staffing', 'manage');
  const [tab, setTab] = useState<'events' | 'types'>('events');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">イベント</h2>
        <p className="text-sm text-text-sub mt-1">
          祭り等、来客数や必要人員が普段と変わるイベントを登録します。シフト作成だけでなく、KPI閾値アラートやAI異常検知の誤報抑止にも使われます。
        </p>
        {tab === 'events'
          ? !canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (登録・編集には自店舗以上の管理権限が必要です)。</p>
          : !canManageTypes && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (種別の登録・編集には全店舗の管理権限が必要です)。</p>}
      </div>

      <div className="border-b border-surface-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="タブ">
          {([['events', 'イベント一覧'], ['types', '種別']] as const).map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} aria-current={tab === key}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                tab === key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
              }`}>
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'events' ? <EventsTab canManage={canManage} /> : <EventTypesTab canManage={canManageTypes} />}
    </div>
  );
}
